import { fetchBffDetailCouponTips } from '@/api/product';
import { IReviewsData } from '@/api/types/product';
import { IProductDetail } from '@/api/types/public';
import CycleMsg from '@/components/CycleMsg/CycleMsg';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import {
  isFaceWidthMeasurementEntryAvailable,
  resolveProductDetailFaceWidthFit,
} from '@/domain/lens/measurement';
import {
  ProductDetailFitsYouStatus,
  ProductDetailMeasurementPrompt,
  ProductDetailSizeWarning,
  useActiveFaceMeasurement,
} from '@/domain/lens/measurement/client';
import BnplLearnMoreDialog from '@/domain/payment/bnpl/BnplLearnMoreDialog';
import { BNPL_DISPLAY_SCENES } from '@/domain/payment/bnpl/constants';
import { useBnplDisplayProducts } from '@/domain/payment/bnpl/useBnplDisplayProducts';
import StarRating from '@/components/StarRating';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { IS_UK } from '@/utils/constants';
import {
  CouponData,
  getPromotionStyle,
  processCouponTips,
  scrollToReviewList,
} from '@/utils/goods-detail-utils';
import { genPrice } from '@/utils/price';
import { DateFormat, DateUtils } from '@/utils/date';
import { getDisplayStars, numberUnitFormat } from '@/utils/tools';
import { Button } from '@mui/material';
import { RightArrowOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useMemo, useState, useRef } from 'react';
import ActivityBanner from '../ActivityBanner';
import CouponSection from '../CouponSection';
import DetailCountDown from '../DetailCountDown';
import MemberTag from '../MemberTag';
import PromotionRules, {
  PromotionData,
  hasPromotionRules,
  PromotionRulesRef,
} from '../PromotionRules';

import { colors } from '@/theme/zeelool.theme';
import { useTranslations } from '@/i18n/use-translations';
import Relations from '../Relations';
import ClientPrice from './ClientPrice';
import PromotionScheduleTag from './PromotionScheduleTag';

const DetailCouponDialog = dynamic(() => import('../dialog/DetailCouponDialog'), { ssr: false });

interface ProductInfoProps {
  product: IProductDetail;
  countDown?: number;
  getGlassesFilter: string;
  refreshPageData: () => void;
  onShowSizeGuide: () => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  SDProductDetailsPageRelatedMktExp?: (params: any, index: number) => void;
  SDProductDetailsPageRelatedMktClick?: (params: any, index: number) => void;
  clientRenderReady: boolean;
  reviewsData: IReviewsData;
  promotionScheduleData?: any;
}

// 主组件
const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  clientRenderReady,
  countDown,
  getGlassesFilter,
  refreshPageData,
  onShowSizeGuide,
  SDProductDetailsPageBtnClick,
  SDProductDetailsPageRelatedMktExp,
  SDProductDetailsPageRelatedMktClick,
  reviewsData,
  promotionScheduleData,
}) => {
  const currency = useStore((state) => state.currency);
  const currencyList = useStore((state) => state.currencyList);
  const userInfo = useStore((state) => state.userInfo);
  const waiMode = useStore((state) => state.waiMode);
  const [showCouponsDialog, setShowCouponsDialog] = useState(false);
  const [couponData, setCouponData] = useState<any>({});
  const [couponTips, setCouponTips] = useState<string>('');
  const [bnplDialogOpen, setBnplDialogOpen] = useState(false);
  const promotionRulesRef = useRef<PromotionRulesRef>(null);
  const pureT = useTranslations('goodsDetail');
  const commonT = useTranslations('common');
  const tSizeGuide = useTranslations('goodsDetail.sizeGuide');
  const t = useTranslations('goodsDetail');
  const measurementT = useTranslations('lensMeasurement');
  const faceMeasurement = useActiveFaceMeasurement();
  const faceWidthFitStatus = resolveProductDetailFaceWidthFit(
    product.product_size,
    faceMeasurement?.recommendedFrameSizes ?? [],
  );
  const faceWidthFitMessage =
    faceWidthFitStatus === 'fits'
      ? measurementT('detail.fitsYou')
      : faceWidthFitStatus === 'too-wide'
        ? measurementT('detail.tooWide')
        : faceWidthFitStatus === 'too-narrow'
          ? measurementT('detail.tooNarrow')
          : null;
  const faceWidthResultDialogMessages = faceMeasurement
    ? {
        title: measurementT('dialog.result.title'),
        latestMeasurement: measurementT('detail.latestMeasurement', {
          date: DateUtils.format(faceMeasurement.measuredAt, DateFormat.DATE_DEFAULT),
        }),
        remeasure: measurementT('dialog.result.remeasure'),
        closeLabel: measurementT('detail.resultDialogCloseLabel'),
      }
    : null;
  const { deviceType } = useContext(ResponsiveContext);
  const showFaceWidthMeasurementEntry = isFaceWidthMeasurementEntryAvailable({
    isUk: IS_UK,
    deviceType,
  });
  const { trackEvent } = useReporting();

  // 获取优惠券提示
  const getDetailCouponTips = useCallback(
    async (isLoginSuccess?: boolean) => {
      // 登录成功后的埋点
      if (isLoginSuccess) {
        try {
          trackEvent(
            {
              eventName: 'product_sign',
              properties: {
                event_label: 'Sign_up',
                event_category: 'PRODUCT_DETAIL',
              },
            },
            [ProviderType.GA4],
          );
        } catch (error) {
          logger.log(error);
        }
      }

      try {
        const res = await fetchBffDetailCouponTips();
        if (res.code === 200) {
          const couponResponse = res.data as CouponData;
          setCouponData(couponResponse);

          // 使用工具函数处理优惠券提示
          const tips = processCouponTips(couponResponse, currencyList, pureT);
          if (tips) {
            setCouponTips(tips);
          }
        }
      } catch (error) {
        logger.log(error);
      }
    },
    [currencyList, pureT, trackEvent],
  );

  useEffectWithCurReady(() => {
    getDetailCouponTips();
  }, [getDetailCouponTips]);

  // 扩展产品对象
  const extendedProduct = product as any;
  // 计算priceInfo
  const priceInfo = useMemo(() => {
    // 会员价
    if (
      userInfo?.is_member === 1 &&
      product &&
      product[`price_info_v${userInfo.member_level}`] &&
      product[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      return genPrice(product[`price_info_v${userInfo.member_level}`], currency.name);
    }
    if (product && product.price_info) {
      return genPrice(product.price_info, currency.name);
    }
    return {};
  }, [product, userInfo, currency]);

  // ========= 分期付款相关逻辑. 仅小语种 =========
  const { bnplText, bnplProducts, bnplShow } = useBnplDisplayProducts({
    scene: BNPL_DISPLAY_SCENES.PRODUCT_DETAIL,
    amount: priceInfo.sell_price,
  });
  // ========= 分期付款相关逻辑. 仅小语种 =========

  // 计算促销类型
  const promotionData = useMemo((): PromotionData => {
    const promotionsType = String(product.products_promotions_type);

    if (promotionsType === '3') return { type: 'seckill' };
    if (promotionsType === '4') return { type: 'promotion' };
    if (userInfo?.is_member === 1 && product[`discount_percent_v${userInfo.member_level}`] > 0) {
      return { type: 'member' };
    }
    if (promotionsType === '2') return { type: 'flash-sale' };
    if (['0', '1'].includes(promotionsType) && userInfo?.is_member && userInfo.is_member !== 1) {
      return { type: 'no-member' };
    }
    if (userInfo?.is_member === 1 && product[`discount_percent_v${userInfo.member_level}`] <= 0) {
      return { type: 'ordinary' };
    }
    return { type: 'tourist' };
  }, [product, userInfo]);

  // 判断是否显示优惠券标签
  const showCouponTag = useMemo(() => {
    // 正常状态 ，是会员，但是不满足门槛
    const rule1 = promotionData.type === 'ordinary';
    // 游客状态
    const rule2 = promotionData.type === 'tourist';
    // 会员状态
    const rule3 = promotionData.type === 'member';
    // flash sale 或是秒杀的情况下 非会员不展示
    const rule4 =
      ['seckill', 'flash-sale', 'promotion'].includes(promotionData.type) &&
      userInfo?.is_member !== 2;
    return rule1 || rule2 || rule3 || rule4;
  }, [promotionData.type, userInfo]);

  const sizeFilter = useCallback((size: string) => {
    switch (size) {
      case 'S':
        return 'Narrow';
      case 'M':
        return 'Medium';
      case 'L':
        return 'Wide';
      default:
        return size;
    }
  }, []);

  const showPromotionFlag = useMemo(() => {
    return (
      ['flash-sale', 'seckill', 'promotion'].includes(promotionData.type) ||
      [3, 4].includes(Number(product.products_promotions_type))
    );
  }, [promotionData.type, product.products_promotions_type]);

  // 是否有促销规则（决定是否显示箭头）
  const hasRules = useMemo(
    () => hasPromotionRules(promotionData, extendedProduct),
    [promotionData, extendedProduct],
  );

  // 渲染活动横幅（scene=2）
  const renderActivityBanners = useMemo(() => {
    if (!product?.commodity_details_label || !Array.isArray(product.commodity_details_label)) {
      return null;
    }

    return product.commodity_details_label
      .filter((item) => item.type === 'activity' && item.scene === 2 && item.status === 1)
      .map((item, index) => (
        <div className="mt-22" key={index}>
          <ActivityBanner
            key={`activity-banner-${index}`}
            item={item}
            SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          />
        </div>
      ));
  }, [product, SDProductDetailsPageBtnClick]);

  return (
    <div className="w-full bg-white">
      <div className="px-[14px] pb-[14px] relative bg-white">
        {/* 促销标签 */}
        {showPromotionFlag && (
          <div
            className={`h-[20px] flex items-center w-[fit-content] rounded-[4px] overflow-hidden mt-[14px] ${
              countDown && countDown > 0 && clientRenderReady ? 'border pr-[4px]' : ''
            }`}
            style={{
              ...(countDown && countDown > 0 && clientRenderReady
                ? {
                    borderColor:
                      extendedProduct.activity_style?.flash_killing_countdown_border_color ||
                      '#FCA6FF',
                  }
                : {}),
            }}
            onClick={() => {
              if (hasRules) {
                promotionRulesRef.current?.openPopup();
                // SDProductDetailsPageBtnClick?.('Promotion Rules');
              }
            }}
          >
            <div
              className="flex items-center h-[20px]"
              style={getPromotionStyle(product as IProductDetail)}
            >
              <span className="text-primary-black font-bs text-[12px] px-[6px]">
                {promotionData.type === 'flash-sale'
                  ? t('flashSale')
                  : extendedProduct.activity_style?.flash_killing_title}
              </span>
              {!(countDown && countDown > 0 && clientRenderReady) && hasRules ? (
                <RightArrowOutlined className="text-[13px] text-[#808080] mr-[4px]" />
              ) : null}
            </div>
            {countDown && countDown > 0 && clientRenderReady ? (
              <>
                <div className="pl-[8px] min-w-[103px]">
                  <DetailCountDown
                    time={countDown}
                    refreshPageData={refreshPageData}
                    variant="light"
                  />
                </div>
                {hasRules && <RightArrowOutlined className="text-[13px] text-[#808080]" />}
              </>
            ) : null}
          </div>
        )}
        {/* 商品决策区固定顺序：基础信息 > 颜色 / 价格 > 尺寸 */}
        {(() => {
          // 基础信息部分（商品基本信息 + 评价）
          const basicInfoSection = (
            <div className="bg-white pt-[16px]">
              <div className="flex items-center text-primary-black leading-[17px]">
                <h1 className="text-[14px] font-bb">{product.short_name}</h1>
                <h2
                  className={`pl-[8px] text-[12px] capitalize ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
                >
                  <span>{product.product_frame_shape}</span>{' '}
                  <span>{product.product_frame_color_name}</span>{' '}
                  <span>{product.product_frame_material}</span> <span>{getGlassesFilter}</span>
                </h2>
              </div>
              {/* 评价 */}
              {reviewsData?.stars && (
                <div
                  className="flex items-center pt-[6px] cursor-pointer w-[fit-content]"
                  onClick={() => {
                    scrollToReviewList(45);
                    SDProductDetailsPageBtnClick?.('View More Reviews');
                  }}
                >
                  <StarRating
                    value={getDisplayStars(reviewsData.stars)}
                    precision={0.5}
                    size="small"
                    iconSize="12px"
                    gap="4px"
                  />
                  <div
                    className={`ml-[6px] text-[12px] underline ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
                  >
                    <span className="">{Number(reviewsData.stars).toFixed(1)}</span>
                    {reviewsData.all_count > 0 && (
                      <span className="ml-[4px]">({numberUnitFormat(reviewsData.all_count)})</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );

          // 关联商品部分（颜色选择）
          const relationsSection =
            product.correlation && product.correlation.length > 0 ? (
              <div className="bg-white">
                <Relations
                  list={product.correlation}
                  product={product}
                  onRelatedClick={(item, index) => {
                    logger.log('Related product clicked:', item, index);
                    SDProductDetailsPageRelatedMktClick?.(item, index);
                  }}
                  onRelatedExposure={(item, index) => {
                    logger.log('Related product exposed:', item, index);
                    SDProductDetailsPageRelatedMktExp?.(item, index);
                  }}
                />
              </div>
            ) : null;

          // 价格部分（价格 + 会员标签 + 优惠券 + 促销规则 + 分期付款）
          const priceSection = (
            <>
              <div className="flex items-center gap-[9px] pb-[4px] pt-[15px]">
                <ClientPrice
                  product={product}
                  refreshPageData={refreshPageData}
                  clientRenderReady={clientRenderReady}
                />
                {/* 促销活动标签 */}
                <PromotionScheduleTag
                  data={promotionScheduleData}
                  SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
                />
              </div>
              {/* 会员标签 */}
              <MemberTag
                userInfo={userInfo}
                priceInfo={priceInfo}
                currency={currency}
                promotionType={promotionData.type}
                product={product}
              />
              {/* 优惠券和积分 */}
              <CouponSection
                showCouponTag={showCouponTag}
                couponTips={couponTips || pureT('coupon.percentOffFirstOrder', { percent: 15 })}
                product={product}
                onShowCoupons={() => setShowCouponsDialog(true)}
                SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              />
              {/* 分期付款 */}
              {bnplShow && (
                <section>
                  {bnplText ? (
                    <div className="item-staging flex items-center pt-[4px] text-[11px] text-[#808080]">
                      {bnplText}{' '}
                      <div
                        className="ml-[4px] underline cursor-pointer"
                        onClick={() => setBnplDialogOpen(true)}
                      >
                        {commonT('text.learnMore')}
                      </div>
                    </div>
                  ) : (
                    <CycleMsg
                      price={priceInfo.sell_price || 0}
                      SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
                      variant="new"
                    />
                  )}
                </section>
              )}
            </>
          );

          // 尺寸信息部分
          const sizeSection =
            product.is_glass === 1 ? (
              <>
                <div className="flex justify-between items-center bg-white pt-[20px] text-[14px]">
                  <div className="flex min-w-0 items-center gap-[8px]">
                    {product.product_size ? (
                      <p className="text-primary-black">
                        <span className="font-bb mr-[5px]">Size:</span>
                        <span>{sizeFilter(product.product_size)}</span>(
                        <span>{Math.round(Number(product?.glass_width))}</span>-
                        <span>{Math.round(Number(product?.glass_height))}</span>-
                        <span>{Math.round(Number(product?.frame_leg_length))}</span>{' '}
                        {tSizeGuide('mm')})
                      </p>
                    ) : null}
                  </div>
                  <Button
                    size="small"
                    onClick={() => {
                      onShowSizeGuide();
                      SDProductDetailsPageBtnClick?.('Size Chart');
                    }}
                    className={`flex items-center text-[12px] normal-case ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
                    endIcon={<RightArrowOutlined />}
                    sx={{
                      padding: '0px',
                      color: waiMode ? colors.primaryBlack : '#808080',
                      fontSize: '3.2vw',
                      '& .MuiButton-endIcon': {
                        marginLeft: '4px',
                        fontSize: '14px',
                        color: '#808080',
                      },
                    }}
                  >
                    {t('sizeChart')}
                  </Button>
                </div>
                {showFaceWidthMeasurementEntry &&
                faceMeasurement &&
                faceWidthResultDialogMessages &&
                faceWidthFitStatus === 'fits' &&
                faceWidthFitMessage ? (
                  <ProductDetailFitsYouStatus
                    measurement={faceMeasurement}
                    dialogMessages={faceWidthResultDialogMessages}
                  >
                    {faceWidthFitMessage}
                  </ProductDetailFitsYouStatus>
                ) : null}
                {showFaceWidthMeasurementEntry ? (
                  faceMeasurement && faceWidthResultDialogMessages ? (
                    (faceWidthFitStatus === 'too-wide' || faceWidthFitStatus === 'too-narrow') &&
                    faceWidthFitMessage ? (
                      <ProductDetailSizeWarning
                        status={faceWidthFitStatus}
                        measurement={faceMeasurement}
                        dialogMessages={faceWidthResultDialogMessages}
                      >
                        {faceWidthFitMessage}
                      </ProductDetailSizeWarning>
                    ) : null
                  ) : (
                    <ProductDetailMeasurementPrompt
                      prompt={measurementT('detail.prompt')}
                      action={measurementT('detail.action')}
                      closeLabel={measurementT('detail.closeLabel')}
                    />
                  )
                ) : null}
              </>
            ) : null;

          return (
            <>
              {basicInfoSection}
              {relationsSection}
              {priceSection}
              {sizeSection}
            </>
          );
        })()}
        {/* 活动横幅（scene=2） */}
        {renderActivityBanners}
        {/* 优惠券对话框 */}
        <DetailCouponDialog
          showCouponsDialog={showCouponsDialog}
          setShowCouponsDialog={setShowCouponsDialog}
          couponData={couponData}
        />
        {/* 促销规则弹窗 */}
        {hasRules && (
          <PromotionRules
            ref={promotionRulesRef}
            promotionData={promotionData}
            product={extendedProduct}
            displayMode="popup"
            onRulesClick={() => SDProductDetailsPageBtnClick?.('Promotion Rules')}
          />
        )}
        <BnplLearnMoreDialog
          open={bnplDialogOpen}
          products={bnplProducts}
          amount={priceInfo.sell_price}
          currency={currency}
          onClose={() => setBnplDialogOpen(false)}
        />
      </div>
    </div>
  );
};

export default ProductInfo;
